import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  MeteringPointTechnicalCalculation,
  MeteringPointTechnicalCalculationPayload,
  TechnicalCalculation,
} from './types';

export const meteringPointTechnicalCalculationApi = createApi({
  reducerPath: 'meteringPointTechnicalCalculationApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/metering-point-technical-calculations`,
  }),
  tagTypes: ['MeteringPointTechnicalCalculation', 'TechnicalCalculation'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getMeteringPointTechnicalCalculations: builder.query<
      MeteringPointTechnicalCalculation,
      MeteringPointTechnicalCalculationPayload
    >({
      query: data => ({
        url: '/query',
        method: 'POST',
        data,
      }),
      providesTags: () => [
        'TechnicalCalculation',
        'MeteringPointTechnicalCalculation',
      ],
    }),
    getMeteringPointTechnicalCalculationById: builder.query<
      MeteringPointTechnicalCalculation,
      string
    >({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => [
        'TechnicalCalculation',
        'MeteringPointTechnicalCalculation',
      ],
    }),
    createMeteringPointTechnicalCalculation: builder.mutation<
      TechnicalCalculation,
      MeteringPointTechnicalCalculation
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringPointTechnicalCalculation',
        { type: 'TechnicalCalculation', id: result?.id },
      ],
    }),
    updateMeteringPointTechnicalCalculationById: builder.mutation<
      TechnicalCalculation,
      TechnicalCalculation
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringPointTechnicalCalculation',
        { type: 'TechnicalCalculation', id: result?.id },
      ],
    }),
    deleteMeteringPointTechnicalCalculationById: builder.mutation<
      TechnicalCalculation,
      string
    >({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: result => [
        'MeteringPointTechnicalCalculation',
        { type: 'TechnicalCalculation', id: result?.id },
      ],
    }),
  }),
});

export const {
  useGetMeteringPointTechnicalCalculationsQuery,
  useCreateMeteringPointTechnicalCalculationMutation,
  useGetMeteringPointTechnicalCalculationByIdQuery,
  useUpdateMeteringPointTechnicalCalculationByIdMutation,
  useDeleteMeteringPointTechnicalCalculationByIdMutation,
} = meteringPointTechnicalCalculationApi;
